import { apiFetch } from "./apiFetch";

export interface VideoPlatformMeeting {
  id: string;
  externalMeetingId: string;
  title: string;
  description: string;
  status: string;
  type: string;
  createdBy: string;
  hostId: string;
  createdAt: string;
  meetingCreatedAt: string;
  /** Effective scheduled start (scheduledStartTime ?? startedAt ?? createdAt). */
  startDate?: string;
  /** Effective scheduled end (scheduledEndTime ?? endedAt). */
  endDate?: string;
  startedAt: string;
  endedAt: string;
  duration: number;
  durationMinutes: number;
  originalMeetingId: string;
  versionNumber: number;
  category?: string;
  isPublicVisible?: boolean;
  fallbackMeetUrl?: string;
  useFallback?: boolean;
}

export interface MeetingCategoryOption {
  id: string;
  title: string;
}

export interface CapabilityControl {
  value: string;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: string;
}

/** Per-attendee capability axes. */
export enum CapabilityAxis {
  Audio = "audio",
  Video = "video",
  Content = "content",
  Chat = "chat",
}

/** Top-level batch operations applied to every attendee in a meeting. */
export enum BatchCapabilityOperation {
  MuteAll = "mute_all",
  UnmuteAll = "unmute_all",
  DisableVideoAll = "disable_video_all",
  EnableVideoAll = "enable_video_all",
  DisableContentAll = "disable_content_all",
  EnableContentAll = "enable_content_all",
  MuteChatAll = "mute_chat_all",
  UnmuteChatAll = "unmute_chat_all",
}

/** Per-axis capability values used by the host UI when toggling locks. */
export enum CapabilityValue {
  /** Default unlocked state — the attendee controls the axis themselves. */
  SendReceive = "SendReceive",
  /** Locked state — disables and prevents the attendee from re-enabling. */
  None = "None",
}

export interface MeetingAttendee {
  id: string;
  externalMeetingId: string;
  attendeeId: string;
  userId: string;
  externalUserId: string;
  attendeeName: string;
  joinedAt: string;
  leftAt: string;
  isActive: boolean;
  capabilities: {
    audio: CapabilityControl;
    video: CapabilityControl;
    content: CapabilityControl;
    chat: CapabilityControl;
  };
  /** True when a MeetingKick row exists for this user in the meeting. */
  isBanned: boolean;
}

export interface Paging {
  count: number;
  total?: number;
  limit: number;
  nextToken?: string | null;
}

export interface MeetingsListResponse {
  data: VideoPlatformMeeting[];
  paging: Paging;
  filters?: Record<string, any>;
}

export interface AttendeesListResponse {
  data: MeetingAttendee[];
  paging: Paging;
}

export const VideoPlatformResource = {
  getMeetings: (params?: {
    status?: string;
    createdBy?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    nextToken?: string;
    prioritizeStatus?: string;
    sortBy?: string;
    sortOrder?: string;
  }) =>
    apiFetch<MeetingsListResponse>("/admin/video-platform/meetings", {
      searchParams: params,
    }),

  getMeeting: (id: string) =>
    apiFetch<VideoPlatformMeeting>(`/admin/video-platform/meetings/${id}`),

  createMeeting: (data: Record<string, any>) =>
    apiFetch<VideoPlatformMeeting>("/admin/video-platform/meetings", {
      method: "POST",
      body: data,
    }),

  deleteMeeting: (id: string) =>
    apiFetch<VideoPlatformMeeting>(`/admin/video-platform/meetings/${id}`, {
      method: "DELETE",
    }),

  getMeetingAttendees: (params: {
    meetingId: string;
    active?: boolean;
    limit?: number;
    nextToken?: string;
  }) => {
    const { meetingId, ...searchParams } = params;
    return apiFetch<AttendeesListResponse>(
      `/admin/video-platform/meetings/${meetingId}/attendees`,
      { searchParams }
    );
  },

  updateAttendeeCapabilities: (
    meetingId: string,
    attendeeId: string,
    capabilities: {
      audio?: { value?: string; lock?: boolean };
      video?: { value?: string; lock?: boolean };
      content?: { value?: string; lock?: boolean };
      chat?: { value?: string; lock?: boolean };
    }
  ) =>
    apiFetch<{
      attendeeId: string;
      userId: string;
      audioCapability: CapabilityControl;
      videoCapability: CapabilityControl;
      contentCapability: CapabilityControl;
      chatCapability: CapabilityControl;
    }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/${attendeeId}/capabilities`,
      { method: "PATCH", body: capabilities }
    ),

  batchUpdateAttendeeCapabilities: (
    meetingId: string,
    body: {
      operation: BatchCapabilityOperation;
      excludeHosts: boolean;
      lock: boolean;
    }
  ) =>
    apiFetch<{ updatedCount: number; attendeeIds: string[] }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/batch`,
      { method: "PATCH", body }
    ),

  /** Ban (kick) an attendee from a meeting — admin-only. */
  banAttendee: (meetingId: string, attendeeId: string, reason?: string) =>
    apiFetch<{
      meetingId: string;
      kickedUserId: string;
      alreadyKicked: boolean;
    }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/${attendeeId}/kick`,
      { method: "POST", body: reason ? { reason } : {} }
    ),

  /** Lift a ban (remove the kick row) so the user can rejoin. Idempotent. */
  unbanAttendee: (meetingId: string, attendeeId: string) =>
    apiFetch<{
      meetingId: string;
      unbannedUserId: string;
      wasBanned: boolean;
    }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/${attendeeId}/kick`,
      { method: "DELETE" }
    ),

  getCategories: () =>
    apiFetch<{ categories: MeetingCategoryOption[] }>(
      "/admin/video-platform/meetings/categories"
    ),

  updateMeeting: (
    id: string,
    data: {
      category?: string;
      title?: string;
      description?: string;
      isPublicVisible?: boolean;
      useFallback?: boolean;
      fallbackMeetUrl?: string | null;
    }
  ) =>
    apiFetch<{
      id: string;
      externalMeetingId: string;
      title: string;
      category?: string;
      status: string;
      isPublicVisible?: boolean;
    }>(`/admin/video-platform/meetings/${id}`, {
      method: "PATCH",
      body: data,
    }),
};
